import { lazy, Suspense } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute";
import { PublicRoute } from "./PublicRoute";
import Loader from "../Components/Loader";
import UpdateApp from "../pages/updateapp/UpdateApp";

// Entry screens stay eager so the very first paint has no lazy-load flash.
import Login from "../pages/auth/Login";
import LanguageSelector from "../pages/auth/LanguageSelector";

// Every other screen is code-split: React.lazy + dynamic import() makes webpack
// emit a separate JS chunk per screen, loaded on demand. This keeps the initial
// bundle from carrying every screen (and its heavy libs like charts / PDF) up
// front, so the login and language screens load fast.
const Password = lazy(() => import("../pages/auth/Password"));
const ForgotPassword = lazy(() => import("../pages/auth/ForgotPassword"));
const VerifyOtp = lazy(() => import("../pages/auth/VerifyOtp"));
const ResetPassword = lazy(() => import("../pages/auth/ResetPassword"));

const PlanExpired = lazy(() => import("../pages/PlanExpired"));
const Profile = lazy(() => import("../pages/profile/Profile"));
const UserProfileEdit = lazy(() => import("../pages/profile/UserProfileEdit"));
const HelpGuide = lazy(() => import("../pages/HelpGuide"));
const ChangePassword = lazy(() => import("../pages/ChangePassword"));
const DeleteAccount = lazy(() => import("../pages/DeleteAccount"));
const StartScan = lazy(() => import("../pages/StartScan"));
const Dashboard = lazy(() => import("../pages/Dashboard"));
const ViewDetailsScreen = lazy(
  () => import("../pages/scanDetails/ViewDetailsScreen"),
);
const Documents = lazy(() => import("../pages/documents/Documents"));
const UploadDocument = lazy(() => import("../pages/documents/UploadDocument"));
const History = lazy(() => import("../pages/History"));
const PrivacyPolicy = lazy(() => import("../pages/PrivacyPolicy"));
const Articles = lazy(() => import("../pages/articles/Articles"));
const ArticlesDetails = lazy(() => import("../pages/articles/ArticlesDetails"));
const UserScanDetails = lazy(
  () => import("../pages/scanDetails/UserScanDetails"),
);
const TermsAndConditions = lazy(() => import("../pages/TermsAndConditions"));
const AboutUs = lazy(() => import("../pages/AboutUs"));
const WellnessInterests = lazy(
  () => import("../pages/auth/SignUp/WellnessInterests"),
);
const ExportScanApprove = lazy(() => import("../pages/ExportScanApprove"));
const ExportDocument = lazy(() => import("../pages/ExportDocument"));
const ViewExportList = lazy(() => import("../pages/ViewExportList"));
const Notification = lazy(() => import("../pages/Notification"));
const DeleteAccountV2 = lazy(() => import("../pages/DeleteAccount-v2"));
const GuestScan = lazy(() => import("../pages/GuestScan"));
const GuestHistory = lazy(() => import("../pages/GuestHistory"));
const GuestList = lazy(() => import("../pages/GuestList"));
const SurveyForm = lazy(() => import("../pages/survey/SurveyForm"));
const ListCampaign = lazy(() => import("../pages/campaign/ListCampaign"));
const CreateCampaign = lazy(() => import("../pages/campaign/CreateCampaign"));
const DetailsCampaign = lazy(() => import("../pages/campaign/DetailsCampaign"));
const CampaignScan = lazy(() => import("../pages/campaign/CampaignScan"));
const ProductDetails = lazy(() => import("../pages/survey/ProductDetails"));
const AccountDetails = lazy(
  () => import("../pages/auth/SignUp/AccountDetails"),
);
const PhysicalProfile = lazy(
  () => import("../pages/auth/SignUp/PhysicalProfile"),
);
const LifestyleQuestions = lazy(
  () => import("../pages/auth/SignUp/LifestyleQuestions"),
);

const WellnessPriorities = lazy(
  () => import("../pages/auth/SignUp/WellnessPriorities"),
);
const QrScanner = lazy(() => import("../pages/QrScanner"));
const ViewQuestions = lazy(() => import("../pages/profile/ViewQuestions"));
const FourLifeLogin = lazy(() => import("../pages/auth/FourLifeLogin"));
const AskAI = lazy(() => import("../pages/AskAI"));
const Dashboard2 = lazy(() => import("../pages/Dashboard2"));
const ShowAllResult = lazy(() => import("../pages/ShowAllResult"));
const ShowAllResult2 = lazy(() => import("../pages/ShowAllResult2"));
const FourLifeScanHistory = lazy(
  () => import("../pages/scanDetails/FourLifeScanHistory"),
);
const BPCalibration = lazy(() => import("../pages/BPCalibration"));

const AppRouter = () => {
  return (
    // Suspense shows the Loader while a lazily-loaded screen's chunk is fetched.
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route
          path="/"
          element={
            <PublicRoute>
              <LanguageSelector />
            </PublicRoute>
          }
        />
        <Route
          path="/update-app"
          element={
            <UpdateApp />
          }
        />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/login-v2"
          element={
            <PublicRoute>
              <FourLifeLogin />
            </PublicRoute>
          }
        />
        <Route
          path="/password"
          element={
            <PublicRoute>
              <Password />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/verify-otp"
          element={
            <PublicRoute>
              <VerifyOtp />
            </PublicRoute>
          }
        />

        <Route
          path="/signup/wellness-interests"
          element={
            <PublicRoute>
              <WellnessInterests />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/qr-scanner"
          element={
            <PublicRoute>
              <QrScanner />
            </PublicRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard2"
          element={
            <ProtectedRoute>
              <Dashboard2 />
            </ProtectedRoute>
          }
        />
        <Route
          path="/show-all-result"
          element={
            <ProtectedRoute>
              <ShowAllResult />
            </ProtectedRoute>
          }
        />
        <Route
          path="/show-all-result-2"
          element={
            <ProtectedRoute>
              <ShowAllResult2 />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile-update"
          element={
            <ProtectedRoute>
              <UserProfileEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path="/language-selection"
          element={
            <ProtectedRoute>
              <LanguageSelector />
            </ProtectedRoute>
          }
        />
        <Route
          path="/deleteAcc-one"
          element={
            <ProtectedRoute>
              <DeleteAccount />
            </ProtectedRoute>
          }
        />
        <Route
          path="/delete-account"
          element={
            <PublicRoute>
              <DeleteAccountV2 />
            </PublicRoute>
          }
        />
        <Route
          path="/help-guide"
          element={
            <ProtectedRoute>
              <HelpGuide />
            </ProtectedRoute>
          }
        />
        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <ChangePassword />
            </ProtectedRoute>
          }
        />

        <Route
          path="/start-scan"
          element={
            <ProtectedRoute>
              <StartScan />
            </ProtectedRoute>
          }
        />
        <Route
          path="/view-more-details"
          element={
            <ProtectedRoute>
              <ViewDetailsScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <Documents />
            </ProtectedRoute>
          }
        />

        <Route
          path="/upload-document"
          element={
            <ProtectedRoute>
              <UploadDocument />
            </ProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={
            <ProtectedRoute>
              <History />
            </ProtectedRoute>
          }
        />
        <Route />
        <Route
          path="/privacy-policy"
          element={
            <ProtectedRoute>
              <PrivacyPolicy />
            </ProtectedRoute>
          }
        />
        <Route />
        <Route
          path="/terms-condition"
          element={
            <ProtectedRoute>
              <TermsAndConditions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/articles"
          element={
            <ProtectedRoute>
              <Articles />
            </ProtectedRoute>
          }
        />
        <Route
          path="/articles/:id"
          element={
            <ProtectedRoute>
              <ArticlesDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-scan-details"
          element={
            <ProtectedRoute>
              <UserScanDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-scan-details-four-life"
          element={
            <ProtectedRoute>
              <FourLifeScanHistory />
            </ProtectedRoute>
          }
        />

        <Route
          path="/about-us"
          element={
            <ProtectedRoute>
              <AboutUs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/privacy-consent"
          element={
            <ProtectedRoute>
              <ExportScanApprove />
            </ProtectedRoute>
          }
        />

        <Route
          path="/export-document"
          element={
            <ProtectedRoute>
              <ExportDocument />
            </ProtectedRoute>
          }
        />
        <Route
          path="/view-export"
          element={
            <ProtectedRoute>
              <ViewExportList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notification"
          element={
            <ProtectedRoute>
              <Notification />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guest-scan"
          element={
            <ProtectedRoute>
              <GuestScan />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guest-history"
          element={
            <ProtectedRoute>
              <GuestHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guest-list"
          element={
            <ProtectedRoute>
              <GuestList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/survey-form"
          element={
            // <ProtectedRoute>
            <SurveyForm />
            // </ProtectedRoute>
          }
        />

        <Route
          path="/product-details"
          element={
            <ProtectedRoute>
              <ProductDetails />
            </ProtectedRoute>
          }
        />

        <Route
          path="/invites-list"
          element={
            <ProtectedRoute>
              <ListCampaign />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invite-create"
          element={
            <ProtectedRoute>
              <CreateCampaign />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invite-details/:campaignCode"
          element={
            <ProtectedRoute>
              <DetailsCampaign />
            </ProtectedRoute>
          }
        />
        <Route path="/invite/:campaignCode" element={<CampaignScan />} />

        <Route
          path="/view-questions"
          element={
            <ProtectedRoute>
              <ViewQuestions />
            </ProtectedRoute>
          }
        />

        <Route
          path="/plan-expired"
          element={
            <ProtectedRoute>
              <PlanExpired />
            </ProtectedRoute>
          }
        />

        {/* SignUp */}

        <Route
          path="/signup/account-details"
          element={
            <PublicRoute>
              <AccountDetails />
            </PublicRoute>
          }
        />
        <Route
          path="/signup/physical-profile"
          element={
            <PublicRoute>
              <PhysicalProfile />
            </PublicRoute>
          }
        />
        <Route
          path="/signup/lifestyle"
          element={
            <PublicRoute>
              <LifestyleQuestions />
            </PublicRoute>
          }
        />

        <Route
          path="/signup/wellness-priorities"
          element={
            <PublicRoute>
              <WellnessPriorities />
            </PublicRoute>
          }
        />
        <Route
          path="/ask-ai"
          element={
            <ProtectedRoute>
              <AskAI />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bp-calibration"
          element={
            <ProtectedRoute>
              <BPCalibration />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
